import { faBook, faBuilding, faUser, faPlusCircle } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React, { FC } from 'react';
import styled, { css } from 'styled-components';

import { IItem } from '../../../../entities/IFilters';
import { EFilterType, EProfileType } from '../../../../entities/IGlobal';
import { colorStack } from '../../../../styleHelpers/colors';
import { fontSizeAndHeight } from '../../../../styleHelpers/fontSizes';
import { Avatar, EAvatarSize } from '../../Avatar/Avatar';
import { Button, EButtonTypeSchema } from '../../Buttons/NewButton';
import { ESpinnerSize, Spinner } from '../../Spinner/Spinner';
import { MultilineTextWithEllipsis } from '../../TextWithEllipsis/MultilineTextWithEllipsis';
import { EButtonsTypes } from '../CorePicker';
import IntlMessage from '../../IntlMessage';
import ScrollBox from '../../ScrollBox/ScrollBox';

const Wrapper = styled.div`
    border-radius: 8px;
    margin-top: 4px;
    overflow: hidden;
    background: ${colorStack.white};
    box-shadow: 0px 4px 12px 0px rgba(0, 0, 0, 0.16);
    > div {
        > div:last-child {
            right: 4px!important;
        }
    }
`;

const NoResult = styled.div`
    height: 62px;
    display: flex;
    align-items: center;
    justify-content: center;
    ${fontSizeAndHeight[16]};
    color: ${colorStack.content};
`;

const SingleElem = styled.div<{ active?: boolean; disabled?: boolean }>`
    font-weight: ${({ active }) => active ? 500 : 400};
    padding: 12px 1rem;
    min-height: 46px;
    justify-content: space-between;
    background: ${colorStack.white};
    transition: all .3s;
    cursor: pointer;
    position: relative;
    display: flex;
    align-items: center;
    &:hover {
        background: ${colorStack.lightBlue};
    }
    ${props => props.active && css`
        background: ${colorStack.lightBlue};
    `}
    ${props => props.disabled && css`
        cursor: not-allowed;
        &:hover {
            background: ${colorStack.whiteGrey};
        }
    `}
    > div:first-child {
        width: calc(100% - .5rem - 34px);
    }
    > div:nth-child(2) {
        width: calc(100% - 35px);
    }
`;

const ShadowLayer = styled.div<{ disabled?: boolean }>`
    ${props => props.disabled && css`
        cursor: not-allowed;
    `}
    width: 97%!important;
    height: 100%;
    position: absolute;
    left: 0;
    top: 0;
    z-index: 999;
`;

const ItemBody = styled.div`
    display: flex;
    align-items: center;
    gap: 1rem;
`;

const ItemMain = styled.div<{ halfWidth: boolean }>`
    flex: 0 1 auto;
    min-width: 0;
    ${props => props.halfWidth && css`
        max-width: 50%;
    `}
`;

const ItemAside = styled.div`
    flex: 1 1 auto;
    min-width: 0;
`;

const ItemAvatar = styled.div<{ disabled?: boolean }>`
    min-width: 0;
    ${props => props.disabled && css`
        opacity: .5;
        filter: grayscale(1);
    `}
`;

const ItemContext = styled(ItemMain)`
    display: flex;
    align-items: center;
    gap: .75rem;
    flex: 1 1 auto;
    ${props => props.halfWidth && css`
        flex: 0 0 50%;
    `}
`;

const ItemContextAvatar = styled(ItemAvatar)`
    flex-shrink: 0;
`;

const ItemContextText = styled.div`
    display: flex;
    flex-direction: column;
    flex: 1 1 auto;
    min-width: 0;
`;

const ItemContextName = styled.div`
    ${fontSizeAndHeight[13]};
    color: ${colorStack.content};
    font-weight: 700;
`;

const AddElem = styled.div`
    padding: 12px 1rem;
    height: 56px;
    background: ${colorStack.bodyBg};
    cursor: pointer;
    display: flex;
    align-items: center;
    gap: 1rem;
    button {
        margin: 0 0 0 auto;
    }
`;

const Icon = styled.div`
    width: 40px;
    display: flex;
    align-items: center;
    justify-content: center;
    height: 40px;
    border-radius: 100%;
    background: ${colorStack.lightBlue};
    font-size: 1rem;
`;

const Texts = styled.div`
    display: flex;
    flex-direction: column;
    width: calc(100% - 56px - 100px);
    > span {
        ${fontSizeAndHeight[16]};
        font-weight: 600;
    }
    > span:nth-of-type(2) {
        font-weight: 400;
    }
    > div {
        ${fontSizeAndHeight[16]};
    }
`;

const DoubleCreationWrapper = styled.div`
    padding: 12px 1rem;
    background: ${colorStack.bodyBg};
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
`;

const SingleCreateWrapper = styled.div`
    display: flex;
    align-items: center;
    gap: 0.5rem;
    padding: 8px 12px;
    border-radius: 4px;
    cursor: pointer;
    transition: background 0.2s;
    &:hover {
        background: ${colorStack.lightBlue};
    }
    svg {
        color: ${colorStack.middleBlue};
    }
    span {
        ${fontSizeAndHeight[16]};
        font-weight: 500;
        color: ${colorStack.middleBlue};
    }
`;

interface IProps {
    items: IItem[];
    selectedItems: IItem[];
    type: EFilterType;
    inputText: string;
    multiSelect?: boolean;
    buttonsType?: EButtonsTypes;
    loading?: boolean;
    hideButtons?: boolean;
    hasMore?: boolean;
    resultsMaxHeight?: number;
    onScrollResults?(): void;
    onSelectItem(item: IItem);
    userFormHandler();
    companyFormHandler();
    themeFormHandler();
    clientReferenceFormHandler?();
    areaOfResponsibilityFormHandler();
    getDisabledItemReason?(data: any): string | undefined;
    renderItemDetails?(data: any): React.ReactNode;
    renderItemAside?(data: any): React.ReactNode;
    renderItemHeader?(data: any): React.ReactNode;
}

export const SearchResults: FC<React.PropsWithChildren<IProps>> = props => {

    const selectItem = (elem: IItem) => {
        props.onSelectItem(elem);
    };

    const selectShadowItem = (item: IItem, e?: React.MouseEvent<HTMLDivElement>) => {
        e?.stopPropagation();
        e?.preventDefault();
        if (props.getDisabledItemReason?.(item.data)) {
            return;
        }
        selectItem(item);
    };

    const hasCustomItemLayout = !!(props.getDisabledItemReason || props.renderItemDetails || props.renderItemAside || props.renderItemHeader);

    return (
        <Wrapper>
            <ScrollBox
                hasMore={!!props.hasMore && !props.loading}
                withoutChildrenCount
                onScrollResults={props.onScrollResults || (() => undefined)}
                autoHeight
                autoHeightMax={props.resultsMaxHeight || 192}
            >
                {
                    <>
                        {props.loading ? (
                            <>
                                <br />
                                <Spinner size={ESpinnerSize.SMALL} />
                                <br />
                            </>
                        ) : (
                            <div>
                                {props.items?.length > 0 ? (
                                    <>
                                        {props.items?.map(elem => {
                                            const disabledReason = props.getDisabledItemReason?.(elem.data);
                                            const itemAside = props.renderItemAside?.(elem.data);
                                            const itemHeader = props.renderItemHeader?.(elem.data);
                                            const itemDetails = (props.renderItemHeader || props.renderItemAside) ? props.renderItemDetails?.(elem.data) : undefined;
                                            const itemContent = (
                                                <>
                                                    {(elem.data?.type === EProfileType.Personal || elem.data?.type === EProfileType.LegalEntity || elem.data?.type === EProfileType.Group || elem.data?.type === EProfileType.Cluster) &&
                                                        <Avatar
                                                            extended
                                                            firstName={elem.text}
                                                            lastName=""
                                                            type={elem.data?.type as EProfileType}
                                                            id={elem.id}
                                                            metaInfo={elem.data?.emailContact}
                                                            boldedName
                                                            size={EAvatarSize.SMALL}
                                                            picture={elem.data?.picture}
                                                            preventRedirection
                                                        />
                                                    }
                                                    {(elem.data?.type === 'default' || !elem.data?.type) &&
                                                        <MultilineTextWithEllipsis numberOfLines={2}>
                                                            {elem.text}
                                                        </MultilineTextWithEllipsis>
                                                    }
                                                </>
                                            );
                                            return (
                                                <SingleElem
                                                    key={elem.id}
                                                    active={!!props.selectedItems?.find(elem2 => elem2.id === elem.id)}
                                                    disabled={!!disabledReason}
                                                >
                                                    <ShadowLayer disabled={!!disabledReason} title={disabledReason} onClick={(e) => selectShadowItem(elem, e)} />
                                                    {(props.renderItemHeader || props.renderItemAside) ? (
                                                        <ItemBody>
                                                            <ItemContext halfWidth={!!props.renderItemAside}>
                                                                <ItemContextAvatar disabled={!!disabledReason}>
                                                                    <Avatar
                                                                        firstName={elem.text}
                                                                        lastName=""
                                                                        type={elem.data?.type as EProfileType}
                                                                        id={elem.id}
                                                                        size={EAvatarSize.SMALL}
                                                                        picture={elem.data?.picture}
                                                                        preventRedirection
                                                                    />
                                                                </ItemContextAvatar>
                                                                <ItemContextText>
                                                                    {itemHeader}
                                                                    <ItemAvatar disabled={!!disabledReason}>
                                                                        <ItemContextName>
                                                                            <MultilineTextWithEllipsis numberOfLines={1}>
                                                                                {elem.text}
                                                                            </MultilineTextWithEllipsis>
                                                                        </ItemContextName>
                                                                    </ItemAvatar>
                                                                    {itemDetails}
                                                                </ItemContextText>
                                                            </ItemContext>
                                                            {itemAside && <ItemAside>{itemAside}</ItemAside>}
                                                        </ItemBody>
                                                    ) : hasCustomItemLayout ? (
                                                        <ItemBody>
                                                            <ItemMain halfWidth={!!itemAside}>
                                                                <ItemAvatar disabled={!!disabledReason}>
                                                                    {itemContent}
                                                                </ItemAvatar>
                                                                {props.renderItemDetails?.(elem.data)}
                                                            </ItemMain>
                                                            {itemAside && <ItemAside>{itemAside}</ItemAside>}
                                                        </ItemBody>
                                                    ) : itemContent}
                                                </SingleElem>
                                            );
                                        })}
                                    </>
                                ) : (
                                    <NoResult>
                                        <IntlMessage id="global.noResultsFound" />
                                    </NoResult>
                                )}
                            </div>
                        )}
                    </>
                }
            </ScrollBox>
            {!props.hideButtons &&
                <>
                    {(props.buttonsType === EButtonsTypes.All && props.inputText?.length >= 1) &&
                        <DoubleCreationWrapper>
                            <SingleCreateWrapper onClick={props.userFormHandler}>
                                <FontAwesomeIcon icon={faPlusCircle} />
                                <IntlMessage id="searchInput.button.createPerson" />
                            </SingleCreateWrapper>
                            <SingleCreateWrapper onClick={props.companyFormHandler}>
                                <FontAwesomeIcon icon={faPlusCircle} />
                                <IntlMessage id="searchInput.button.createLegalEntity" />
                            </SingleCreateWrapper>
                        </DoubleCreationWrapper>
                    }
                    {(props.buttonsType === EButtonsTypes.Invite && props.inputText?.length >= 1) &&
                        <AddElem>
                            <Icon>
                                <FontAwesomeIcon icon={faUser} />
                            </Icon>
                            <Texts>
                                <IntlMessage id="global.profileDoesntExist" tagName="span" />
                                <MultilineTextWithEllipsis numberOfLines={1}>{props.inputText}</MultilineTextWithEllipsis>
                            </Texts>
                            <Button typeSchema={EButtonTypeSchema.SECONDARY} onClick={props.userFormHandler}>
                                <IntlMessage id="global.invite" />
                            </Button>
                        </AddElem>
                    }
                    {(props.buttonsType === EButtonsTypes.Person && props.inputText?.length >= 1) &&
                        <AddElem>
                            <Icon>
                                <FontAwesomeIcon icon={faUser} />
                            </Icon>
                            <Texts>
                                <IntlMessage id="global.profileDoesntExist" tagName="span" />
                                <MultilineTextWithEllipsis numberOfLines={1}>{props.inputText}</MultilineTextWithEllipsis>
                            </Texts>
                            <Button typeSchema={EButtonTypeSchema.SECONDARY} onClick={props.userFormHandler}>
                                <IntlMessage id="global.create" />
                            </Button>
                        </AddElem>
                    }
                    {(props.buttonsType === EButtonsTypes.Company && props.inputText?.length >= 1) &&
                        <AddElem>
                            <Icon>
                                <FontAwesomeIcon icon={faBuilding} />
                            </Icon>
                            <Texts>
                                <IntlMessage id="global.leDoesntExist" tagName="span" />
                                <MultilineTextWithEllipsis numberOfLines={1}>{props.inputText}</MultilineTextWithEllipsis>
                            </Texts>
                            <Button typeSchema={EButtonTypeSchema.SECONDARY} onClick={props.companyFormHandler}>
                                <IntlMessage id="global.create" />
                            </Button>
                        </AddElem>
                    }
                    {(props.buttonsType === EButtonsTypes.Theme && props.themeFormHandler && props.inputText?.length > 0 && (props.items.concat(props.selectedItems)).filter(item => item.text?.trim().toLocaleLowerCase() === props.inputText?.trim().toLocaleLowerCase()).length === 0) &&
                        <AddElem>
                            <Icon>
                                <FontAwesomeIcon icon={faBook} />
                            </Icon>
                            <Texts>
                                <IntlMessage id="global.themeDoesntExist" tagName="span" />
                                <span>{props.inputText}</span>
                            </Texts>
                            <Button typeSchema={EButtonTypeSchema.PRIMARY} onClick={props.themeFormHandler}>
                                <IntlMessage id="global.create" />
                            </Button>
                        </AddElem>
                    }
                    {(props.buttonsType === EButtonsTypes.ClientReference && props.clientReferenceFormHandler && props.inputText?.trim().length > 0 && (props.items.concat(props.selectedItems)).filter(item => item.text?.trim().toLocaleLowerCase() === props.inputText?.trim().toLocaleLowerCase()).length === 0) &&
                        <AddElem>
                            <Icon>
                                <FontAwesomeIcon icon={faBook} />
                            </Icon>
                            <Texts>
                                <IntlMessage id="global.clientReferenceDoesntExist" defaultMessage="Client reference doesn't exist, please add it." tagName="span" />
                                <span>{props.inputText}</span>
                            </Texts>
                            <Button typeSchema={EButtonTypeSchema.PRIMARY} onClick={props.clientReferenceFormHandler}>
                                <IntlMessage id="global.create" />
                            </Button>
                        </AddElem>
                    }
                    {(props.buttonsType === EButtonsTypes.AreaOfResponsibility && props.areaOfResponsibilityFormHandler && props.inputText?.length > 0 && props.items.filter(item => item.text?.trim().toLocaleLowerCase() === props.inputText?.trim().toLocaleLowerCase()).length === 0) &&
                        <AddElem>
                            <Icon>
                                <FontAwesomeIcon icon={faBook} />
                            </Icon>
                            <Texts>
                                <IntlMessage id="global.areaOfResponsibilityDoesntExist" tagName="span" />
                                <span>{props.inputText}</span>
                            </Texts>
                            <Button typeSchema={EButtonTypeSchema.PRIMARY} onClick={props.areaOfResponsibilityFormHandler}>
                                <IntlMessage id="global.create" />
                            </Button>
                        </AddElem>
                    }
                </>
            }
        </Wrapper>
    );
};
